import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { Global, css } from '@emotion/react';
import { Info } from 'react-feather';
import LinearProgress from '@mui/material/LinearProgress';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Tooltip } from '@trinity/chat-components/components/presentation/Tooltip.js';
import { Body } from '@trinity/chat-components/components/Modal/Body.js';
import { ButtonSet } from '@trinity/chat-components/components/Modal/ButtonSet.js';
import { Header } from '@trinity/chat-components/components/Modal/Header.js';
import { Modal } from '@trinity/chat-components/components/Modal/Modal.js';
import { ButtonActivityIndicator } from '@trinity/chat-components/components/presentation/Button.ActivityIndicator.js';
import { ButtonOutlined } from '@trinity/chat-components/components/presentation/ButtonOutlined.js';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { Select } from '@/components/presentation/Select';
import {
  parseCsvText,
  validateMessageRows,
  type ParsedMessageRow,
  type ParsedMessageRowWithValidation,
} from './csv-parse';
import { getHackathonBrandNames } from '@trinity/chat/types/hackathon';
import { MESSAGE_CATEGORIES_BASE } from '@trinity/chat';
import { useAppSelector } from '@/store';

interface BulkUploadModalProps {
  open: boolean;
  onClose: () => void;
  brands: string[];
  enableSegmentSelection?: boolean;
  preSelectedSegmentationRunId?: number | null;
  preSelectedSegmentId?: string | null;
}

type Phase = 'idle' | 'preview' | 'uploading' | 'complete';

const MAX_FILE_SIZE = 1024 * 1024;
const BATCH_SIZE = 5;

type UploadResult = {
  succeeded: number;
  failed: Array<{ rowIndex: number; title: string; error: string }>;
};

export function BulkUploadModal({
  open,
  onClose,
  brands,
  enableSegmentSelection = true,
  preSelectedSegmentationRunId = null,
  preSelectedSegmentId = null,
}: BulkUploadModalProps): React.JSX.Element {
  const groupIdentifier = useAppSelector(state => state.group.identifier);
  const hackathonBrandNames = getHackathonBrandNames(groupIdentifier);
  const effectiveBrands = brands.length > 0 ? brands : hackathonBrandNames;

  const CSV_FORMAT_TOOLTIP = `Expected CSV format:
Columns: title, body, category, brand, status

- title (required): Message headline
- body (required): Message content, max 500 chars
- category: Efficacy, Patient selection, Duration, Convenience, Safety, Survival, Unmet needs
- brand: ${hackathonBrandNames.join(', ')} (defaults to Elderberry)
- status: draft (default), approved, published

Fields with commas should be wrapped in "double quotes".`;

  const apiClient = useChatApiClient();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [phase, setPhase] = useState<Phase>('idle');
  const [fileName, setFileName] = useState('');
  const [parseError, setParseError] = useState('');
  const [allRows, setAllRows] = useState<ParsedMessageRowWithValidation[]>([]);
  const [validRows, setValidRows] = useState<ParsedMessageRow[]>([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  const [selectedRunId, setSelectedRunId] = useState<number | null>(preSelectedSegmentationRunId);
  const [selectedSegmentId, setSelectedSegmentId] = useState<string | null>(preSelectedSegmentId);

  useEffect(() => {
    if (!open) {
      setPhase('idle');
      setFileName('');
      setParseError('');
      setAllRows([]);
      setValidRows([]);
      setUploadProgress(0);
      setUploadResult(null);
      setSelectedRunId(preSelectedSegmentationRunId);
      setSelectedSegmentId(preSelectedSegmentId);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [open, preSelectedSegmentationRunId, preSelectedSegmentId]);

  const { data: segmentationRuns = [] } = useQuery({
    queryKey: ['segmentation-runs', { status: 'complete' }],
    queryFn: async () => {
      const response = await apiClient.authenticatedClient.get('/segmentation-runs', {
        params: { status: 'complete', take: 100 },
      });
      return response.data.runs || [];
    },
    enabled: open && enableSegmentSelection,
  });

  const { data: selectedRunData } = useQuery({
    queryKey: ['segmentation-run', selectedRunId],
    queryFn: async () => {
      const response = await apiClient.authenticatedClient.get(`/segmentation-runs/${selectedRunId}`);
      return response.data.run;
    },
    enabled: !!selectedRunId,
  });

  const segments = selectedRunData?.segments || [];

  const validCount = useMemo(() => validRows.length, [validRows]);

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      if (file.size > MAX_FILE_SIZE) {
        setParseError('File is too large. Maximum size is 1MB.');
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        const text = event.target?.result;
        if (typeof text !== 'string') return;

        try {
          const { headers, rows } = parseCsvText(text);
          if (headers.length === 0) {
            setParseError('CSV file is empty or has no header row.');
            return;
          }

          const result = validateMessageRows(headers, rows, MESSAGE_CATEGORIES_BASE, effectiveBrands);
          setAllRows(result.allRows);
          setValidRows(result.validRows);
          setFileName(file.name);
          setParseError('');
          setPhase('preview');
        } catch (err) {
          setParseError(err instanceof Error ? err.message : 'Failed to parse CSV file.');
        }
      };
      reader.readAsText(file, 'utf-8');
    },
    [effectiveBrands]
  );

  const handleChangeFile = useCallback(() => {
    setPhase('idle');
    setAllRows([]);
    setValidRows([]);
    setParseError('');
    setFileName('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handleUpload = useCallback(async () => {
    if (validRows.length === 0) return;

    setPhase('uploading');
    setUploadProgress(0);

    const segmentationSessionId = selectedRunId && selectedRunData ? selectedRunData.sessionId : null;
    const segmentId = selectedSegmentId || null;

    let succeeded = 0;
    const failed: UploadResult['failed'] = [];

    for (let i = 0; i < validRows.length; i += BATCH_SIZE) {
      const batch = validRows.slice(i, i + BATCH_SIZE);
      const results = await Promise.allSettled(
        batch.map((row) =>
          apiClient.messageGeneration.createMessage({
            title: row.title,
            body: row.body,
            category: row.category,
            brand: row.brand,
            status: row.status,
            ...(segmentationSessionId ? { segmentationSessionId } : {}),
            ...(segmentId ? { segmentId } : {}),
          })
        )
      );

      for (let j = 0; j < results.length; j++) {
        const result = results[j];
        const row = batch[j];
        if (result.status === 'fulfilled') {
          succeeded++;
        } else {
          const errorMsg = result.reason instanceof Error ? result.reason.message : 'Unknown error';
          failed.push({
            rowIndex: allRows.findIndex((r) => r.title === row.title && r.body === row.body) + 1,
            title: row.title,
            error: errorMsg,
          });
        }
      }

      setUploadProgress(Math.min(i + BATCH_SIZE, validRows.length));
    }

    setUploadResult({ succeeded, failed });
    setPhase('complete');
    queryClient.invalidateQueries({ queryKey: ['generated-messages'] });
  }, [validRows, allRows, selectedRunId, selectedRunData, selectedSegmentId, apiClient, queryClient]);

  const isUploading = phase === 'uploading';
  const canClose = !isUploading;

  return (
    <>
      {open && (
        <Global
          styles={css`
            body {
              overflow: hidden !important;
              --z-index-modal: 999999 !important;
            }
          `}
        />
      )}
      <StyledModal
        open={open}
        onClose={canClose ? onClose : undefined}
        disableEscapeClose={isUploading}
        disableOverlayClick={isUploading}
        classes={{
          root: 'bulk-upload-modal-root',
          wrap: 'bulk-upload-modal-wrap',
        }}
      >
        <StyledBody>
          <StyledHeader>Bulk Upload Messages</StyledHeader>

          {phase === 'idle' && (
            <FormContainer>
              <InstructionRow>
                <InstructionText>Upload a CSV file with message data.</InstructionText>
                <Tooltip
                  title={CSV_FORMAT_TOOLTIP}
                  tooltipstyles={{ whiteSpace: 'pre-line' as const }}
                  PopperProps={{ style: { zIndex: 9999999 } }}
                >
                  <InfoIconWrapper>
                    <Info size={16} />
                  </InfoIconWrapper>
                </Tooltip>
              </InstructionRow>

              <FileRow>
                <HiddenInput ref={fileInputRef} type="file" accept=".csv" onChange={handleFileSelect} />
                <ChooseFileButton onClick={() => fileInputRef.current?.click()}>
                  Choose CSV File
                </ChooseFileButton>
                <FileLabel>{fileName || 'No file selected'}</FileLabel>
              </FileRow>

              {parseError && <ErrorText>{parseError}</ErrorText>}

              {enableSegmentSelection && (
                <>
                  <FormRow>
                    <Label>Segmentation Run</Label>
                    <Select
                      value={selectedRunId || ''}
                      onChange={(e) => {
                        setSelectedRunId(e.target.value ? Number(e.target.value) : null);
                        setSelectedSegmentId(null);
                      }}
                      placeholder="None"
                      options={segmentationRuns.reduce(
                        (acc: Record<string, string>, run: Record<string, unknown>) => ({
                          ...acc,
                          [String(run.id)]:
                            `${(run.projectTitle as string) || (run.name as string) || (run.runKey as string)} (${new Date(
                              run.createdOn as string
                            ).toLocaleDateString()})`,
                        }),
                        {}
                      )}
                    />
                  </FormRow>

                  {selectedRunId && (
                    <FormRow>
                      <Label>Segment</Label>
                      <Select
                        value={selectedSegmentId || ''}
                        onChange={(e) => setSelectedSegmentId(e.target.value as string)}
                        disabled={!segments.length}
                        placeholder="None"
                        options={segments.reduce(
                          (acc: Record<string, string>, segment: Record<string, unknown>) => ({
                            ...acc,
                            [segment.identifier as string]: segment.name as string,
                          }),
                          {}
                        )}
                      />
                    </FormRow>
                  )}
                </>
              )}
            </FormContainer>
          )}

          {phase === 'preview' && (
            <FormContainer>
              <PreviewHeader>
                <FileInfo>
                  {fileName} — {allRows.length} rows parsed
                </FileInfo>
                <ChangeFileLink onClick={handleChangeFile}>Change File</ChangeFileLink>
              </PreviewHeader>

              {enableSegmentSelection && (
                <>
                  <FormRow>
                    <Label>Segmentation Run</Label>
                    <Select
                      value={selectedRunId || ''}
                      onChange={(e) => {
                        setSelectedRunId(e.target.value ? Number(e.target.value) : null);
                        setSelectedSegmentId(null);
                      }}
                      placeholder="None"
                      options={segmentationRuns.reduce(
                        (acc: Record<string, string>, run: Record<string, unknown>) => ({
                          ...acc,
                          [String(run.id)]:
                            `${(run.projectTitle as string) || (run.name as string) || (run.runKey as string)} (${new Date(
                              run.createdOn as string
                            ).toLocaleDateString()})`,
                        }),
                        {}
                      )}
                    />
                  </FormRow>

                  {selectedRunId && (
                    <FormRow>
                      <Label>Segment</Label>
                      <Select
                        value={selectedSegmentId || ''}
                        onChange={(e) => setSelectedSegmentId(e.target.value as string)}
                        disabled={!segments.length}
                        placeholder="None"
                        options={segments.reduce(
                          (acc: Record<string, string>, segment: Record<string, unknown>) => ({
                            ...acc,
                            [segment.identifier as string]: segment.name as string,
                          }),
                          {}
                        )}
                      />
                    </FormRow>
                  )}
                </>
              )}

              <SummaryText>
                <ValidCount>{validCount}</ValidCount> of {allRows.length} messages are valid.
              </SummaryText>

              <PreviewTableWrapper>
                <PreviewTable>
                  <thead>
                    <tr>
                      <Th>Row</Th>
                      <Th>Title</Th>
                      <Th>Body</Th>
                      <Th>Category</Th>
                      <Th>Brand</Th>
                      <Th>Status</Th>
                      <Th></Th>
                    </tr>
                  </thead>
                  <tbody>
                    {allRows.map((row) => (
                      <PreviewRow key={row.rowIndex} $hasError={!row.isValid}>
                        <Td>{row.rowIndex}</Td>
                        <Td title={row.title}>{truncate(row.title, 30)}</Td>
                        <Td title={row.body}>{truncate(row.body, 40)}</Td>
                        <Td>{row.category || '-'}</Td>
                        <Td>{row.brand || '-'}</Td>
                        <Td>{row.status}</Td>
                        <Td>
                          {row.isValid ? (
                            <ValidIcon>&#10003;</ValidIcon>
                          ) : (
                            <Tooltip
                              title={row.errors.join('\n')}
                              tooltipstyles={{ whiteSpace: 'pre-line' as const }}
                              PopperProps={{ style: { zIndex: 9999999 } }}
                            >
                              <ErrorIcon>&#10007;</ErrorIcon>
                            </Tooltip>
                          )}
                        </Td>
                      </PreviewRow>
                    ))}
                  </tbody>
                </PreviewTable>
              </PreviewTableWrapper>
            </FormContainer>
          )}

          {phase === 'uploading' && (
            <FormContainer>
              <ProgressText>
                Uploading messages... {uploadProgress} of {validCount}
              </ProgressText>
              <StyledLinearProgress
                variant="determinate"
                value={validCount > 0 ? (uploadProgress / validCount) * 100 : 0}
              />
            </FormContainer>
          )}

          {phase === 'complete' && uploadResult && (
            <FormContainer>
              <ResultTitle>Upload Complete</ResultTitle>
              <ResultText>
                {uploadResult.succeeded} message{uploadResult.succeeded !== 1 ? 's' : ''} created
                successfully.
              </ResultText>
              {uploadResult.failed.length > 0 && (
                <>
                  <ErrorResultText>
                    {uploadResult.failed.length} message{uploadResult.failed.length !== 1 ? 's' : ''} failed.
                  </ErrorResultText>
                  <FailedList>
                    {uploadResult.failed.map((f, i) => (
                      <FailedItem key={i}>
                        Row {f.rowIndex} ("{truncate(f.title, 30)}"): {f.error}
                      </FailedItem>
                    ))}
                  </FailedList>
                </>
              )}
            </FormContainer>
          )}
        </StyledBody>

        <StyledButtonSet>
          {phase === 'idle' && (
            <ButtonOutlined borderWidth="thin" color="silver" onClick={onClose}>
              Cancel
            </ButtonOutlined>
          )}
          {phase === 'preview' && (
            <>
              <ButtonOutlined borderWidth="thin" color="silver" onClick={onClose}>
                Cancel
              </ButtonOutlined>
              <ButtonActivityIndicator
                color="primary"
                indicatorSize={12}
                disabled={validCount === 0}
                onClick={handleUpload}
              >
                Upload {validCount} Message{validCount !== 1 ? 's' : ''}
              </ButtonActivityIndicator>
            </>
          )}
          {phase === 'complete' && (
            <ButtonActivityIndicator color="primary" indicatorSize={12} onClick={onClose}>
              Done
            </ButtonActivityIndicator>
          )}
        </StyledButtonSet>
      </StyledModal>
    </>
  );
}

function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
}

const StyledModal = styled(Modal)`
  &.bulk-upload-modal-root {
    z-index: 999999 !important;
    width: fit-content !important;
    max-width: 90vw !important;
    height: fit-content !important;
    overflow-y: auto !important;
  }

  .bulk-upload-modal-wrap {
    padding: 0 !important;
    position: relative;

    > div:first-of-type {
      padding: 4px 8px 0 0;

      > div {
        font-size: 18px;
        line-height: 18px;
      }
    }
  }
`;

const StyledBody = styled(Body)`
  padding: 0;
  overflow: visible;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const StyledHeader = styled(Header)`
  color: var(--black);
  font-family: var(--font-bold);
  font-size: 22px;
  font-weight: 600;
  text-align: center;
  margin: 0;
  padding: 12px 0 8px 0;
  width: 100%;
`;

const StyledButtonSet = styled(ButtonSet)`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  padding: 8px 0 12px 0;

  button {
    min-width: 130px;
  }
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 0 40px 10px 40px;
  width: fit-content;
  min-width: 600px;
  max-width: 800px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: 12px;
  align-items: center;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: var(--black-l);
  font-family: var(--font-bold);
  text-align: right;
  white-space: nowrap;
`;

const InstructionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
`;

const InstructionText = styled.span`
  font-size: 14px;
  color: var(--gray-d);
`;

const InfoIconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  color: #6366f1;
  cursor: help;
`;

const FileRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const HiddenInput = styled.input`
  display: none;
`;

const ChooseFileButton = styled.button`
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 500;
  color: #6366f1;
  background: white;
  border: 1px solid #6366f1;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    background: #eef2ff;
  }
`;

const FileLabel = styled.span`
  font-size: 13px;
  color: #666;
`;

const ErrorText = styled.div`
  font-size: 13px;
  color: #d32f2f;
  padding: 6px 10px;
  background: #fef2f2;
  border-radius: 4px;
`;

const PreviewHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const FileInfo = styled.span`
  font-size: 13px;
  color: #333;
  font-weight: 500;
`;

const ChangeFileLink = styled.button`
  font-size: 12px;
  color: #6366f1;
  background: none;
  border: none;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;

  &:hover {
    color: #4f46e5;
  }
`;

const SummaryText = styled.div`
  font-size: 13px;
  color: #333;
`;

const ValidCount = styled.span`
  font-weight: 600;
  color: #059669;
`;

const PreviewTableWrapper = styled.div`
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
`;

const PreviewTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
`;

const Th = styled.th`
  text-align: left;
  padding: 6px 8px;
  background: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;
  font-weight: 600;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: #1a1a1a;
  position: sticky;
  top: 0;
  z-index: 1;
`;

const PreviewRow = styled.tr<{ $hasError: boolean }>`
  background: ${(props) => (props.$hasError ? '#fef2f2' : 'white')};

  &:hover {
    background: ${(props) => (props.$hasError ? '#fee2e2' : '#f9f9f9')};
  }
`;

const Td = styled.td`
  padding: 6px 8px;
  border-bottom: 1px solid #f0f0f0;
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ValidIcon = styled.span`
  color: #059669;
  font-weight: 600;
`;

const ErrorIcon = styled.span`
  color: #d32f2f;
  font-weight: 600;
  cursor: help;
`;

const ProgressText = styled.div`
  font-size: 14px;
  color: #333;
  text-align: center;
  padding: 20px 0 8px;
`;

const StyledLinearProgress = styled(LinearProgress)`
  border-radius: 4px;
  height: 8px;
  margin-bottom: 20px;
`;

const ResultTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #1a1a1a;
  text-align: center;
  padding-top: 10px;
`;

const ResultText = styled.div`
  font-size: 14px;
  color: #059669;
  text-align: center;
`;

const ErrorResultText = styled.div`
  font-size: 14px;
  color: #d32f2f;
  text-align: center;
`;

const FailedList = styled.div`
  max-height: 150px;
  overflow-y: auto;
  padding: 8px;
  background: #fef2f2;
  border-radius: 4px;
  margin-bottom: 8px;
`;

const FailedItem = styled.div`
  font-size: 12px;
  color: #991b1b;
  padding: 2px 0;
`;
